/**
 * LIFF Product Page - Compact View for LINE Keep
 *
 * This page displays a product in LIFF view (compact/tall/full)
 * Designed for sharing via LINE Keep/Notes with smooth UX
 *
 * Usage:
 * 1. Generate LIFF URL: https://liff.line.me/{liffId}?path=/good/{id}
 * 2. Append &view=compact for compact view
 * 3. Share via LINE Keep, Notes, or direct message
 *
 * DEBUG MODE:
 * - Set DEBUG_MODE = true to show debug panel and allow browser testing
 * - Set DEBUG_MODE = false for production (redirects to home if not in LINE)
 */

// 🔧 DEBUG MODE: Set to true to enable browser testing
const DEBUG_MODE = false;

import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useLiff } from '@misterlex223/cloudflare-line-liff-sdk/react';
import { useLiffConfig } from '../../providers/LiffProvider';
import { getProductById } from '../../services/productsApi';
import { useCart } from '../../contexts/CartContext';
import type { Product, ProductVariant } from '../../types';
import './LiffProductPage.css';

interface LiffContextInfo {
  viewType: 'compact' | 'tall' | 'full';
  isInClient: boolean;
  userId?: string;
}

const LiffProductPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const config = useLiffConfig();
  const { addToCart } = useCart();

  // Product state
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [addToCartMessage, setAddToCartMessage] = useState<string | null>(null);
  const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string>>({});

  // LIFF state
  const [liffContext, setLiffContext] = useState<LiffContextInfo | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [debugStep, setDebugStep] = useState('等待初始化');
  const hasInitialized = useRef(false);

  // Initialize LIFF SDK
  const liff = useLiff({
    ...config,
    onInitSuccess: () => {
      console.log('[LiffProductPage] LIFF initialized successfully');
      setDebugStep('LIFF 初始化成功');

      const inClient = liff.isInClient();
      const context = liff.getContext();
      const viewType = context.viewType === 'compact' ? 'compact' : context.viewType === 'tall' ? 'tall' : 'full';

      setLiffContext({
        viewType,
        isInClient: inClient,
        userId: context.userId,
      });

      // Add LIFF-specific classes
      if (inClient) {
        document.body.classList.add('is-liff');
        document.body.classList.add(`liff-view-${viewType}`);
      }

      setIsReady(true);
    },
    onInitError: (error) => {
      console.error('[LiffProductPage] LIFF initialization failed:', error);
      setDebugStep(`LIFF 初始化失敗: ${error.message}`);
      setError('LIFF 初始化失敗');
      setIsReady(true);
    },
  });

  // Initialize LIFF
  useEffect(() => {
    const initLiff = async () => {
      if (hasInitialized.current) return;
      hasInitialized.current = true;
      setDebugStep('正在初始化 LIFF...');

      try {
        if (!liff.isInitialized && !liff.isLoading) {
          await liff.init();
        }
      } catch (error) {
        console.error('[LiffProductPage] LIFF init error:', error);
        setDebugStep(`LIFF init 錯誤: ${error instanceof Error ? error.message : '未知錯誤'}`);
        setError('LIFF 初始化失敗');
        setIsReady(true);
      }
    };

    initLiff();
  }, []);

  // Redirect to regular product page if not in LINE App
  useEffect(() => {
    if (isReady && liffContext && !liffContext.isInClient && !DEBUG_MODE) {
      // Not in LINE App, redirect to regular product page
      console.log('[LiffProductPage] Not in LINE App, redirecting to /products/:id');
      window.location.href = `/products/${id}`;
    }
  }, [isReady, liffContext, id]);

  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) return;

      setLoading(true);
      setError(null);

      try {
        const response = await getProductById(id);

        if ('success' in response && response.success) {
          setProduct(response.data);
        } else {
          setError('找不到商品');
        }
      } catch (err: any) {
        console.error('[LiffProductPage] Failed to fetch product:', err);
        setError(err?.message || '載入商品資料失敗');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  // ============ Variant Logic ============

  const getAttributeOptions = () => {
    if (!product?.variants || product.variants.length === 0) return {};

    const attributeMap: Record<string, Set<string>> = {};

    product.variants.forEach(variant => {
      Object.entries(variant.attributes).forEach(([key, value]) => {
        if (!attributeMap[key]) {
          attributeMap[key] = new Set();
        }
        attributeMap[key].add(value);
      });
    });

    const result: Record<string, string[]> = {};
    Object.entries(attributeMap).forEach(([key, values]) => {
      result[key] = Array.from(values).sort();
    });

    return result;
  };

  const findMatchingVariant = (): ProductVariant | null => {
    if (!product?.variants || product.variants.length === 0) return null;

    if (Object.keys(selectedAttributes).length === 0) {
      return null;
    }

    const matching = product.variants.find(variant => {
      return Object.entries(selectedAttributes).every(([key, value]) => {
        return variant.attributes[key] === value;
      });
    });

    return matching || null;
  };

  const handleAttributeSelect = (attributeName: string, value: string) => {
    const newAttributes = { ...selectedAttributes, [attributeName]: value };
    const attributeKeys = Object.keys(getAttributeOptions());
    const currentIndex = attributeKeys.indexOf(attributeName);

    if (currentIndex >= 0) {
      const resetAttributes: Record<string, string> = {};
      attributeKeys.slice(0, currentIndex + 1).forEach(key => {
        if (newAttributes[key]) {
          resetAttributes[key] = newAttributes[key];
        }
      });
      setSelectedAttributes(resetAttributes);
    } else {
      setSelectedAttributes(newAttributes);
    }
  };

  const activeVariant = findMatchingVariant();
  const displayVariant = activeVariant;
  const displayPrice = displayVariant?.salePrice || displayVariant?.regularPrice || product?.salePrice || product?.regularPrice;
  const hasSalePrice = displayVariant
    ? (displayVariant.salePrice !== null && displayVariant.regularPrice !== null && displayVariant.salePrice < displayVariant.regularPrice)
    : (product && product.salePrice !== null && product.regularPrice !== null && product.salePrice < product.regularPrice);

  const currentStockStatus = displayVariant?.stockStatus || product?.stockStatus;
  const currentStockQuantity = displayVariant?.stockQuantity || product?.stockQuantity || 0;

  // Image list
  const allImages = displayVariant?.imageUrls && displayVariant.imageUrls.length > 0
    ? displayVariant.imageUrls
    : (product?.featuredImageUrl
      ? [product.featuredImageUrl, ...product.imageUrls.filter(url => url !== product.featuredImageUrl)]
      : product?.imageUrls || []);

  // Handlers
  const handleAddToCart = async () => {
    if (!product) return;

    setAddingToCart(true);
    setAddToCartMessage(null);

    try {
      await addToCart(product.id, activeVariant?.id, activeVariant?.attributes, quantity);
      setAddToCartMessage('已加入購物車！');

      // In LIFF, we can show a success message or close window
      setTimeout(() => {
        if (liffContext?.isInClient) {
          window.close(); // Close the LIFF window
        }
        setAddToCartMessage(null);
      }, 1500);
    } catch (err: any) {
      setAddToCartMessage(err.message || '加入購物車失敗');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleBuyNow = async () => {
    if (!product) return;

    setAddingToCart(true);
    setAddToCartMessage(null);

    try {
      await addToCart(product.id, activeVariant?.id, activeVariant?.attributes, quantity);
      // Navigate to checkout - close LIFF and open checkout in browser
      if (liffContext?.isInClient) {
        window.close();
      }
      setTimeout(() => {
        window.location.href = '/checkout';
      }, 500);
    } catch (err: any) {
      setAddToCartMessage(err.message || '加入購物車失敗');
      setAddingToCart(false);
    }
  };

  const handleShareToTalk = async () => {
    if (!product) return;

    try {
      // Use Web Share API if available
      if (navigator.share) {
        await navigator.share({
          title: product.title,
          text: product.shortDescription || '',
          url: window.location.href,
        });
      }
    } catch (err) {
      console.error('[LiffProductPage] Share failed:', err);
    }
  };

  // Loading state
  if (!isReady || loading) {
    return (
      <div className="liff-product-page liff-product-page--loading">
        <div className="liff-product-page__loader"></div>
        <p>載入中...</p>
        {DEBUG_MODE && (
          <div className="liff-product-page__debug">
            <p>{debugStep}</p>
            <p>LIFF ID: {config.liffId || '未設置'}</p>
            <p>Product ID: {id}</p>
          </div>
        )}
      </div>
    );
  }

  // Error state
  if (error || !product) {
    return (
      <div className="liff-product-page liff-product-page--error">
        <div className="liff-product-page__error-icon">⚠️</div>
        <h2>載入失敗</h2>
        <p>{error || '找不到商品'}</p>
        {DEBUG_MODE && (
          <div className="liff-product-page__debug">
            <p><strong>Debug Info:</strong></p>
            <p>步驟: {debugStep}</p>
            <p>LIFF 已初始化: {liff.isInitialized ? '✓' : '✗'}</p>
            <p>在 LINE App 中: {liffContext?.isInClient ? '✓' : '✗'}</p>
            <p>視圖類型: {liffContext?.viewType || '未知'}</p>
            <p>Product ID: {id}</p>
            <p>LIFF ID: {config.liffId || '未設置'}</p>
            <p>Service URL: {config.serviceUrl || '未設置'}</p>
          </div>
        )}
        <button
          className="liff-product-page__btn"
          onClick={() => liffContext?.isInClient ? window.close() : window.history.back()}
        >
          返回
        </button>
      </div>
    );
  }

  // Attribute options
  const attributeOptions = getAttributeOptions();
  const hasVariants = product.variants && product.variants.length > 0;

  return (
    <div className={`liff-product-page liff-product-page--${liffContext?.viewType || 'full'}`}>
      {/* Header */}
      <div className="liff-product-page__header">
        {liffContext?.isInClient && (
          <button
            className="liff-product-page__close-btn"
            onClick={() => window.close()}
            aria-label="關閉"
          >
            ✕
          </button>
        )}
        <span className="liff-product-page__badge">商品</span>
      </div>

      {/* Content */}
      <div className="liff-product-page__content">
        {/* Images */}
        {allImages.length > 0 && (
          <div className="liff-product-page__images">
            <div className="liff-product-page__main-image">
              <img src={allImages[selectedImageIndex]} alt={product.title} />
              {currentStockStatus === 'outofstock' && (
                <div className="liff-product-page__sold-out">已售完</div>
              )}
            </div>

            {allImages.length > 1 && (
              <div className="liff-product-page__thumbnails">
                {allImages.map((image, index) => (
                  <button
                    key={index}
                    className={`liff-product-page__thumbnail ${index === selectedImageIndex ? 'active' : ''}`}
                    onClick={() => setSelectedImageIndex(index)}
                  >
                    <img src={image} alt={`${product.title} ${index + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Product Info */}
        <div className="liff-product-page__info">
          <h1 className="liff-product-page__title">{product.title}</h1>

          {displayPrice && (
            <div className="liff-product-page__price">
              {hasSalePrice && (displayVariant?.regularPrice || product.regularPrice) && (
                <span className="liff-product-page__price--original">
                  NT${displayVariant?.regularPrice || product.regularPrice}
                </span>
              )}
              <span className="liff-product-page__price--current">
                NT${displayPrice}
              </span>
              {hasSalePrice && (
                <span className="liff-product-page__discount">特價中</span>
              )}
            </div>
          )}

          {/* Variant Selectors */}
          {hasVariants && Object.entries(attributeOptions).length > 0 && (
            <div className="liff-product-page__variants">
              {Object.entries(attributeOptions).map(([attrName, options]) => (
                <div key={attrName} className="liff-product-page__variant-group">
                  <h3>{attrName}</h3>
                  <div className="liff-product-page__variant-options">
                    {options.map(option => {
                      const isSelected = selectedAttributes[attrName] === option;
                      const isAvailable = product.variants!.some(v =>
                        v.attributes[attrName] === option && v.stockStatus === 'instock'
                      );

                      return (
                        <button
                          key={option}
                          className={`liff-product-page__variant-option ${isSelected ? 'selected' : ''} ${!isAvailable ? 'outofstock' : ''}`}
                          onClick={() => handleAttributeSelect(attrName, option)}
                          disabled={!isAvailable}
                          type="button"
                        >
                          {option}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Description */}
          {product.shortDescription && (
            <div className="liff-product-page__short-desc">
              {product.shortDescription}
            </div>
          )}

          {/* Stock Status */}
          <div className="liff-product-page__stock">
            {currentStockStatus === 'instock'
              ? currentStockQuantity > 0 ? `庫存: ${currentStockQuantity} 件` : '有庫存'
              : currentStockStatus === 'outofstock' ? '已售完' : '補貨中'}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="liff-product-page__actions">
        {currentStockStatus !== 'outofstock' ? (
          <>
            <div className="liff-product-page__quantity">
              <label htmlFor="quantity">數量:</label>
              <select
                id="quantity"
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
              >
                {Array.from({ length: Math.min(10, currentStockQuantity || 10) }, (_, i) => i + 1).map(num => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
            </div>

            <button
              className="liff-product-page__btn liff-product-page__btn--primary"
              onClick={handleBuyNow}
              disabled={addingToCart || (hasVariants && !activeVariant)}
            >
              {addingToCart ? '處理中...' : '立即購買'}
            </button>

            <button
              className="liff-product-page__btn liff-product-page__btn--secondary"
              onClick={handleAddToCart}
              disabled={addingToCart || (hasVariants && !activeVariant)}
            >
              {addingToCart ? '加入中...' : '加入購物車'}
            </button>
          </>
        ) : (
          <div className="liff-product-page__out-of-stock">
            此商品已售完
          </div>
        )}

        {liffContext?.isInClient && (
          <button
            className="liff-product-page__btn liff-product-page__btn--share"
            onClick={handleShareToTalk}
          >
            分享給朋友
          </button>
        )}
      </div>

      {/* Message Toast */}
      {addToCartMessage && (
        <div className={`liff-product-page__toast ${addToCartMessage.includes('失敗') ? 'error' : 'success'}`}>
          {addToCartMessage}
        </div>
      )}

      {/* Debug Panel */}
      {DEBUG_MODE && (
        <div className="liff-product-page__debug-panel">
          <h3>🔧 Debug Panel</h3>
          <div className="liff-product-page__debug-section">
            <strong>當前狀態:</strong> {debugStep}
          </div>
          <div className="liff-product-page__debug-section">
            <strong>LIFF 狀態:</strong>
            <ul>
              <li>已初始化: {liff.isInitialized ? '✓' : '✗'}</li>
              <li>載入中: {liff.isLoading ? '✓' : '✗'}</li>
              <li>在 LINE App: {liffContext?.isInClient ? '✓' : '✗'}</li>
              <li>視圖類型: {liffContext?.viewType || '未知'}</li>
            </ul>
          </div>
          <div className="liff-product-page__debug-section">
            <strong>商品資訊:</strong>
            <ul>
              <li>ID: {id}</li>
              <li>標題: {product.title}</li>
              <li>價格: NT${displayPrice}</li>
              <li>庫存: {currentStockStatus}</li>
            </ul>
          </div>
          <div className="liff-product-page__debug-section">
            <strong>LIFF 配置:</strong>
            <ul>
              <li>LIFF ID: {config.liffId || '未設置'}</li>
              <li>App ID: {config.appId || '未設置'}</li>
              <li>Service URL: {config.serviceUrl || '未設置'}</li>
            </ul>
          </div>
          <div className="liff-product-page__debug-section">
            <strong>URL:</strong>
            <code style={{ fontSize: '11px', wordBreak: 'break-all' }}>
              {window.location.href}
            </code>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiffProductPage;
